function queryShadowRoot(root, skipNode, isMatch, maxDepth = 20, depth = 0) {
  let matches = [];
  if (depth >= maxDepth) {
    return matches;
  }
  const traverseSlot = ($slot) => {
    const assignedNodes = $slot.assignedNodes().filter((node) => node.nodeType === 1);
    if (assignedNodes.length > 0) {
      return queryShadowRoot(assignedNodes[0].parentElement, skipNode, isMatch, maxDepth, depth + 1);
    }
    return [];
  };
  const children = Array.from(root.children || []);
  for (const $child of children) {
    if (skipNode($child)) {
      continue;
    }
    if (isMatch($child)) {
      matches.push($child);
    }
    if ($child.shadowRoot != null) {
      matches.push(...queryShadowRoot($child.shadowRoot, skipNode, isMatch, maxDepth, depth + 1));
    } else if ($child.tagName === "SLOT") {
      matches.push(...traverseSlot($child));
    } else {
      matches.push(...queryShadowRoot($child, skipNode, isMatch, maxDepth, depth + 1));
    }
  }
  return matches;
}
function isHidden($elem) {
  return $elem.hasAttribute("hidden") || $elem.hasAttribute("aria-hidden") && $elem.getAttribute("aria-hidden") !== "false" || $elem.style.display === `none` || $elem.style.opacity === `0` || $elem.style.visibility === `hidden` || $elem.style.visibility === `collapse`;
}
function isDisabled($elem) {
  return $elem.hasAttribute("disabled") || $elem.hasAttribute("aria-disabled") && $elem.getAttribute("aria-disabled") !== "false";
}
function isFocusable($elem) {
  if ($elem.getAttribute("tabindex") === "-1" || isHidden($elem) || isDisabled($elem)) {
    return false;
  }
  return (
    // At this point we know that the element can have focus (eg. won't be -1) if the tabindex attribute exists
    $elem.hasAttribute("tabindex") || ($elem instanceof HTMLAnchorElement || $elem instanceof HTMLAreaElement) && $elem.hasAttribute("href") || ($elem instanceof HTMLButtonElement || $elem instanceof HTMLInputElement || $elem instanceof HTMLTextAreaElement || $elem instanceof HTMLSelectElement) || $elem instanceof HTMLIFrameElement
  );
}
const timeouts = /* @__PURE__ */ new Map();
function debounce(cb, ms, id) {
  const timeout = timeouts.get(id);
  if (timeout != null) {
    window.clearTimeout(timeout);
  }
  timeouts.set(id, window.setTimeout(() => {
    cb();
    timeouts.delete(id);
  }, ms));
}
const template = document.createElement("template");
template.innerHTML = `
	<div id="start"></div>
	<div id="backup"></div>
	<slot></slot>
	<div id="end"></div>
`;
class FocusTrap extends HTMLElement {
  /**
   * Attaches the shadow root.
   */
  constructor() {
    super();
    this.debounceId = Math.random().toString();
    this._focused = false;
    const shadow = this.attachShadow({ mode: "open" });
    shadow.appendChild(template.content.cloneNode(true));
    this.$backup = shadow.querySelector("#backup");
    this.$start = shadow.querySelector("#start");
    this.$end = shadow.querySelector("#end");
    this.focusLastElement = this.focusLastElement.bind(this);
    this.focusFirstElement = this.focusFirstElement.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
  }
  // Whenever one of these attributes changes we need to render the template again.
  static get observedAttributes() {
    return [
      "inactive"
    ];
  }
  /**
   * Determines whether the focus trap is active or not.
   * @attr
   */
  get inactive() {
    return this.hasAttribute("inactive");
  }
  set inactive(value) {
    value ? this.setAttribute("inactive", "") : this.removeAttribute("inactive");
  }
  /**
   * Returns whether the element currently has focus.
   */
  get focused() {
    return this._focused;
  }
  /**
   * Hooks up the element.
   */
  connectedCallback() {
    this.$start.addEventListener("focus", this.focusLastElement);
    this.$end.addEventListener("focus", this.focusFirstElement);
    this.addEventListener("focusin", this.onFocusIn);
    this.addEventListener("focusout", this.onFocusOut);
    this.render();
  }
  /**
   * Tears down the element.
   */
  disconnectedCallback() {
    this.$start.removeEventListener("focus", this.focusLastElement);
    this.$end.removeEventListener("focus", this.focusFirstElement);
    this.removeEventListener("focusin", this.onFocusIn);
    this.removeEventListener("focusout", this.onFocusOut);
  }
  /**
   * When the attributes changes we need to re-render the template.
   */
  attributeChangedCallback() {
    this.render();
  }
  /**
   * Focuses the first focusable element in the focus trap.
   */
  focusFirstElement() {
    this.trapFocus();
  }
  /**
   * Focuses the last focusable element in the focus trap.
   */
  focusLastElement() {
    this.trapFocus(true);
  }
  /**
   * Returns a list of the focusable children found within the element.
   */
  getFocusableElements() {
    return queryShadowRoot(this, isHidden, isFocusable);
  }
  /**
   * Focuses on either the last or first focusable element.
   * @param {boolean} trapToEnd
   */
  trapFocus(trapToEnd) {
    if (this.inactive)
      return;
    let focusableChildren = this.getFocusableElements();
    if (focusableChildren.length > 0) {
      if (trapToEnd) {
        focusableChildren[focusableChildren.length - 1].focus();
      } else {
        focusableChildren[0].focus();
      }
      this.$backup.setAttribute("tabindex", "-1");
    } else {
      this.$backup.setAttribute("tabindex", "0");
      this.$backup.focus();
    }
  }
  /**
   * When the element gains focus this function is called.
   */
  onFocusIn() {
    this.updateFocused(true);
  }
  /**
   * When the element looses its focus this function is called.
   */
  onFocusOut() {
    this.updateFocused(false);
  }
  /**
   * Updates the focused property and updates the view.
   * The update is debounced because the focusin and focusout out
   * might fire multiple times in a row. We only want to render
   * the element once, therefore waiting until the focus is "stable".
   * @param value
   */
  updateFocused(value) {
    debounce(() => {
      if (this.focused !== value) {
        this._focused = value;
        this.render();
      }
    }, 0, this.debounceId);
  }
  /**
   * Updates the template.
   */
  render() {
    this.$start.setAttribute("tabindex", !this.focused || this.inactive ? `-1` : `0`);
    this.$end.setAttribute("tabindex", !this.focused || this.inactive ? `-1` : `0`);
    this.focused ? this.setAttribute("focused", "") : this.removeAttribute("focused");
  }
}
window.customElements.define("focus-trap", FocusTrap);
//# sourceMappingURL=focus-trap-10d97a26.js.map
